import React from "react";
import { Container, Card, Button } from "react-bootstrap";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { BsTrash, BsPlus, BsDash } from "react-icons/bs";
import { useCheckout } from "../context/CheckoutContext";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";

const Cart = () => {
  const { cart, removeFromCart, increaseQuantity, decreaseQuantity, totalPrice } = useCart();
  const { proceedToCheckout } = useCheckout();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!user) {
      navigate("/login", { state: { redirectTo: "/checkout" } });
    } else {
      proceedToCheckout(cart);
      navigate("/checkout");
    }
  };

  const pageVariants = {
    initial: { opacity: 0, x: 50 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: -50 },
  };

  const pageTransition = {
    duration: 0.6,
    ease: "easeInOut",
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1 },
    }),
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="in"
      exit="out"
      transition={pageTransition}
    >
      <Container className="py-5 mt-5 px-3 px-md-5">
        <h2 className="text-center fw-bold mb-4" style={{ color: "#25a1d5" }}>
          🛒 Your Shopping Cart
        </h2>

        {cart.length === 0 ? (
          <motion.div
            className="text-center p-5 border rounded shadow-lg bg-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p className="fs-4 fw-semibold text-muted">Your cart is empty!</p>
            <Button
              style={{ backgroundColor: "#25a1d5", borderColor: "#25a1d5" }}
              size="lg"
              onClick={() => navigate("/")}
            >
              Start Shopping 🛍️
            </Button>
          </motion.div>
        ) : (
          <>
            <div className="d-flex flex-column gap-4">
              <AnimatePresence>
                {cart.map((product, i) => (
                  <motion.div
                    key={product.productId}  // Use productId instead of id
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    custom={i}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    <Card className="p-3 shadow border-0 rounded-4 d-flex flex-md-row flex-column align-items-center bg-white">
                      <Card.Img
                        src={
                          product.images?.[0]?.url
                            ? `https://madhusewingmachines.com/uploads/${product.images[0].url}`
                            : "/placeholder.jpg"
                        }

                        className="rounded-3 mb-3 mb-md-0"
                        style={{ height: "180px", width: "180px", objectFit: "contain" }}
                      />


                      <Card.Body className="ms-md-4 text-center text-md-start">
                        <Card.Title className="fw-bold fs-4">{product.name}</Card.Title>
                        <Card.Text className="text-muted">{product.type}</Card.Text>
                        <h5 className="fw-bold text-success">₹{product.price}</h5>

                        <div className="d-flex justify-content-center justify-content-md-start align-items-center gap-3 my-3">
                          <Button
                            variant="outline-primary"
                            className="rounded-circle"
                            onClick={() => decreaseQuantity(product.productId)}  // Use productId instead of id
                          >
                            <BsDash />
                          </Button>
                          <span className="fw-bold fs-5">{product.quantity}</span>
                          <Button
                            variant="outline-primary"
                            className="rounded-circle"
                            onClick={() => increaseQuantity(product.productId)}  // Use productId instead of id
                          >
                            <BsPlus />
                          </Button>
                        </div>

                        <Button
                          variant="danger"
                          className="fw-semibold mt-2"
                          onClick={() => removeFromCart(product.productId)}  // Use productId instead of id
                        >
                          <BsTrash size={18} /> Remove
                        </Button>
                      </Card.Body>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <motion.div
              className="text-center mt-5 p-4 border rounded bg-light shadow-sm"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="fw-bold text-dark">
                Total: <span className="text-success">₹{totalPrice}</span>
              </h3>
              <Button
                style={{ backgroundColor: "#25a1d5", borderColor: "#25a1d5" }}
                size="lg"
                className="fw-bold px-5 py-2 mt-3 shadow-lg"
                onClick={handleCheckout}
              >
                Proceed to Checkout
              </Button>
            </motion.div>
          </>
        )}
      </Container>
    </motion.div>
  );
};

export default Cart;
